import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { OriginateCallRequest } from "@/types";
import { callsApi } from "@/lib/api";

export interface CallHistoryFilters {
  direction?: string;
  date_from?: string;
  date_to?: string;
}

export function useActiveCalls() {
  return useQuery({
    queryKey: ["calls", "active"],
    queryFn: callsApi.active,
    refetchInterval: 5000,
  });
}

export function useCallHistory(skip = 0, limit = 50, filters: CallHistoryFilters = {}) {
  return useQuery({
    queryKey: ["calls", "history", skip, limit, filters],
    queryFn: () => callsApi.history({ skip, limit, ...filters }),
  });
}

export function useCallHistoryCount(filters: CallHistoryFilters = {}) {
  return useQuery({
    queryKey: ["calls", "count", filters],
    queryFn: () => callsApi.historyCount(filters),
  });
}

export function useCall(id: string) {
  return useQuery({
    queryKey: ["calls", id],
    queryFn: () => callsApi.get(id),
    enabled: !!id,
  });
}

export function useHangupCall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (channel: string) => callsApi.hangup(channel),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calls"] });
    },
  });
}

export function useTransferCall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ channel, destination }: { channel: string; destination: string }) =>
      callsApi.transfer(channel, destination),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calls"] });
    },
  });
}

export function useOriginateCall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: OriginateCallRequest) => callsApi.originate(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["calls"] });
    },
  });
}

export function useExtensions() {
  return useQuery({
    queryKey: ["calls", "extensions"],
    queryFn: callsApi.extensions,
  });
}